/**
 * Heir Definitions
 * Complete definitions of all heirs in the Faraid system
 * Based on FaraidInfo.md Section 3 (Waris-Waris)
 */

import type { HeirDefinition, HeirType } from './faraid.types';

/**
 * Complete heir definitions with blocking rules (Hijab)
 * Reference: FaraidInfo.md Section 5 (Pendindingan)
 */
export const HEIR_DEFINITIONS: Record<HeirType, HeirDefinition> = {
  // ============================================================================
  // WARIS UTAMA (Primary Heirs)
  // These heirs are never completely blocked (except spouse by invalid marriage)
  // ============================================================================
  
  suami: {
    type: 'suami',
    labelMs: 'Suami',
    labelEn: 'Husband',
    category: 'waris_utama',
    gender: 'lelaki',
    maxCount: 1,
    canHaveMultiple: false,
    blockedBy: [], // Never blocked
  },
  
  isteri: {
    type: 'isteri',
    labelMs: 'Isteri',
    labelEn: 'Wife',
    category: 'waris_utama',
    gender: 'perempuan',
    maxCount: 4, // Islam allows up to 4 wives
    canHaveMultiple: true,
    blockedBy: [], // Never blocked
  },
  
  bapa: {
    type: 'bapa',
    labelMs: 'Bapa',
    labelEn: 'Father',
    category: 'waris_utama',
    gender: 'lelaki',
    maxCount: 1,
    canHaveMultiple: false,
    blockedBy: [], // Never blocked
  },
  
  ibu: {
    type: 'ibu',
    labelMs: 'Ibu',
    labelEn: 'Mother',
    category: 'waris_utama',
    gender: 'perempuan',
    maxCount: 1,
    canHaveMultiple: false,
    blockedBy: [], // Never blocked
  },
  
  anak_lelaki: {
    type: 'anak_lelaki',
    labelMs: 'Anak Lelaki',
    labelEn: 'Son',
    category: 'waris_utama',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [], // Never blocked
  },
  
  anak_perempuan: {
    type: 'anak_perempuan',
    labelMs: 'Anak Perempuan',
    labelEn: 'Daughter',
    category: 'waris_utama',
    gender: 'perempuan',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [], // Never blocked
  },
  
  // ============================================================================
  // WARIS GANTI (Substitute Heirs) - Grandparents and Grandchildren
  // ============================================================================
  
  datuk: {
    type: 'datuk',
    labelMs: 'Bapa Kepada Bapa (Datuk)',
    labelEn: 'Paternal Grandfather',
    category: 'waris_ganti',
    gender: 'lelaki',
    maxCount: 1,
    canHaveMultiple: false,
    blockedBy: ['bapa'], // Blocked by father
    specialConditions: 'Terdinding oleh Bapa',
  },
  
  nenek_sebelah_bapa: {
    type: 'nenek_sebelah_bapa',
    labelMs: 'Ibu Kepada Bapa (Nenek)',
    labelEn: 'Paternal Grandmother',
    category: 'waris_ganti',
    gender: 'perempuan',
    maxCount: 1,
    canHaveMultiple: false,
    blockedBy: ['ibu', 'bapa'], // Blocked by mother or father
    specialConditions: 'Terdinding oleh Ibu dan Bapa',
  },
  
  nenek_sebelah_ibu: {
    type: 'nenek_sebelah_ibu',
    labelMs: 'Ibu Kepada Ibu (Nenek)',
    labelEn: 'Maternal Grandmother',
    category: 'waris_ganti',
    gender: 'perempuan',
    maxCount: 1,
    canHaveMultiple: false,
    blockedBy: ['ibu'], // Blocked by mother only
    specialConditions: 'Terdinding oleh Ibu',
  },
  
  cucu_lelaki: {
    type: 'cucu_lelaki',
    labelMs: 'Anak Lelaki kepada Anak Lelaki (Cucu Lelaki)',
    labelEn: 'Grandson (through son)',
    category: 'waris_ganti',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['anak_lelaki'], // Blocked by son
    specialConditions: 'Terdinding oleh Anak Lelaki',
  },
  
  cucu_perempuan: {
    type: 'cucu_perempuan',
    labelMs: 'Anak Perempuan kepada Anak Lelaki (Cucu Perempuan)',
    labelEn: 'Granddaughter (through son)',
    category: 'waris_ganti',
    gender: 'perempuan',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['anak_lelaki'], // Blocked by son
    specialConditions: 'Terdinding oleh Anak Lelaki',
  },
  
  cicit_lelaki: {
    type: 'cicit_lelaki',
    labelMs: 'Anak Lelaki kepada Anak Lelaki kepada Anak Lelaki (Cicit Lelaki)',
    labelEn: 'Great-Grandson',
    category: 'waris_ganti',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['anak_lelaki', 'cucu_lelaki'], // Blocked by son or grandson
    specialConditions: 'Terdinding oleh Anak Lelaki atau Cucu Lelaki',
  },
  
  cicit_perempuan: {
    type: 'cicit_perempuan',
    labelMs: 'Anak Perempuan kepada Anak Lelaki kepada Anak Lelaki (Cicit Perempuan)',
    labelEn: 'Great-Granddaughter',
    category: 'waris_ganti',
    gender: 'perempuan',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['anak_lelaki', 'cucu_lelaki'], // Blocked by son or grandson
    specialConditions: 'Terdinding oleh Anak Lelaki atau Cucu Lelaki',
  },
  
  // ============================================================================
  // WARIS KEDUA (Secondary Heirs) - Siblings
  // Reference: FaraidInfo.md Section 5.2 (Pendindingan Semua)
  // ============================================================================
  
  saudara_seibu_sebapa_lelaki: {
    type: 'saudara_seibu_sebapa_lelaki',
    labelMs: 'Saudara Seibu Sebapa Lelaki (SIBL)',
    labelEn: 'Full Brother',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki'],
    specialConditions: 'Terdinding oleh Bapa, Datuk, atau keturunan lelaki ke bawah',
  },
  
  saudara_seibu_sebapa_perempuan: {
    type: 'saudara_seibu_sebapa_perempuan',
    labelMs: 'Saudara Seibu Sebapa Perempuan (SIBP)',
    labelEn: 'Full Sister',
    category: 'waris_kedua',
    gender: 'perempuan',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki'],
    specialConditions: 'Terdinding oleh Bapa, Datuk, atau keturunan lelaki ke bawah',
  },
  
  saudara_sebapa_lelaki: {
    type: 'saudara_sebapa_lelaki',
    labelMs: 'Saudara Sebapa Lelaki (SBL)',
    labelEn: 'Paternal Half-Brother',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'saudara_seibu_sebapa_lelaki'],
    specialConditions: 'Terdinding oleh SIBL, Bapa, Datuk, atau keturunan lelaki ke bawah',
  },
  
  saudara_sebapa_perempuan: {
    type: 'saudara_sebapa_perempuan',
    labelMs: 'Saudara Sebapa Perempuan (SBP)',
    labelEn: 'Paternal Half-Sister',
    category: 'waris_kedua',
    gender: 'perempuan',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'saudara_seibu_sebapa_lelaki'],
    specialConditions: 'Terdinding oleh SIBL, Bapa, Datuk, atau keturunan lelaki ke bawah',
  },
  
  saudara_seibu_lelaki: {
    type: 'saudara_seibu_lelaki',
    labelMs: 'Saudara Seibu Lelaki (SIL)',
    labelEn: 'Maternal Half-Brother',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['bapa', 'datuk', 'anak_lelaki', 'anak_perempuan', 'cucu_lelaki', 'cucu_perempuan'],
    specialConditions: 'Terdinding oleh Bapa, Datuk, atau keturunan ke bawah',
  },
  
  saudara_seibu_perempuan: {
    type: 'saudara_seibu_perempuan',
    labelMs: 'Saudara Seibu Perempuan (SIP)',
    labelEn: 'Maternal Half-Sister',
    category: 'waris_kedua',
    gender: 'perempuan',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: ['bapa', 'datuk', 'anak_lelaki', 'anak_perempuan', 'cucu_lelaki', 'cucu_perempuan'],
    specialConditions: 'Terdinding oleh Bapa, Datuk, atau keturunan ke bawah',
  },
  
  // ============================================================================
  // WARIS KEDUA (Secondary Heirs) - Nephews (Anak Saudara)
  // ============================================================================
  
  anak_saudara_seibu_sebapa_lelaki: {
    type: 'anak_saudara_seibu_sebapa_lelaki',
    labelMs: 'Anak Lelaki kepada SIBL (Anak Saudara)',
    labelEn: 'Son of Full Brother (Nephew)',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  anak_saudara_sebapa_lelaki: {
    type: 'anak_saudara_sebapa_lelaki',
    labelMs: 'Anak Lelaki kepada SBL (Anak Saudara)',
    labelEn: 'Son of Paternal Half-Brother (Nephew)',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  // ============================================================================
  // WARIS KEDUA (Secondary Heirs) - Uncles (Bapa Saudara)
  // ============================================================================
  
  bapa_saudara_seibu_sebapa: {
    type: 'bapa_saudara_seibu_sebapa',
    labelMs: 'SIBL kepada Bapa (Bapa Saudara)',
    labelEn: 'Full Paternal Uncle',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  bapa_saudara_sebapa: {
    type: 'bapa_saudara_sebapa',
    labelMs: 'SBL kepada Bapa (Bapa Saudara)',
    labelEn: 'Paternal Half-Uncle',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki',
      'bapa_saudara_seibu_sebapa'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  // ============================================================================
  // WARIS KEDUA (Secondary Heirs) - Cousins (Sepupu)
  // ============================================================================
  
  anak_bapa_saudara_seibu_sebapa: {
    type: 'anak_bapa_saudara_seibu_sebapa',
    labelMs: 'Anak Lelaki kepada SIBL kepada Bapa (Sepupu)',
    labelEn: 'Son of Full Paternal Uncle (Cousin)',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki',
      'bapa_saudara_seibu_sebapa', 'bapa_saudara_sebapa'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  anak_bapa_saudara_sebapa: {
    type: 'anak_bapa_saudara_sebapa',
    labelMs: 'Anak Lelaki kepada SBL kepada Bapa (Sepupu)',
    labelEn: 'Son of Paternal Half-Uncle (Cousin)',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki',
      'bapa_saudara_seibu_sebapa', 'bapa_saudara_sebapa',
      'anak_bapa_saudara_seibu_sebapa'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  // ============================================================================
  // WARIS KEDUA (Secondary Heirs) - Great-uncles (Datuk Saudara)
  // ============================================================================
  
  datuk_saudara_seibu_sebapa: {
    type: 'datuk_saudara_seibu_sebapa',
    labelMs: 'SIBL Datuk',
    labelEn: 'Full Brother of Grandfather',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki',
      'bapa_saudara_seibu_sebapa', 'bapa_saudara_sebapa',
      'anak_bapa_saudara_seibu_sebapa', 'anak_bapa_saudara_sebapa'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  datuk_saudara_sebapa: {
    type: 'datuk_saudara_sebapa',
    labelMs: 'SBL Datuk',
    labelEn: 'Paternal Half-Brother of Grandfather',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki',
      'bapa_saudara_seibu_sebapa', 'bapa_saudara_sebapa',
      'anak_bapa_saudara_seibu_sebapa', 'anak_bapa_saudara_sebapa',
      'datuk_saudara_seibu_sebapa'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  anak_datuk_saudara_seibu_sebapa: {
    type: 'anak_datuk_saudara_seibu_sebapa',
    labelMs: 'Anak Lelaki SIBL Datuk',
    labelEn: 'Son of Grandfather\'s Full Brother',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki',
      'bapa_saudara_seibu_sebapa', 'bapa_saudara_sebapa',
      'anak_bapa_saudara_seibu_sebapa', 'anak_bapa_saudara_sebapa',
      'datuk_saudara_seibu_sebapa', 'datuk_saudara_sebapa'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
  
  anak_datuk_saudara_sebapa: {
    type: 'anak_datuk_saudara_sebapa',
    labelMs: 'Anak Lelaki SBL Datuk',
    labelEn: 'Son of Grandfather\'s Paternal Half-Brother',
    category: 'waris_kedua',
    gender: 'lelaki',
    maxCount: 99,
    canHaveMultiple: true,
    blockedBy: [
      'bapa', 'datuk', 'anak_lelaki', 'cucu_lelaki', 'cicit_lelaki',
      'saudara_seibu_sebapa_lelaki', 'saudara_sebapa_lelaki',
      'anak_saudara_seibu_sebapa_lelaki', 'anak_saudara_sebapa_lelaki',
      'bapa_saudara_seibu_sebapa', 'bapa_saudara_sebapa',
      'anak_bapa_saudara_seibu_sebapa', 'anak_bapa_saudara_sebapa',
      'datuk_saudara_seibu_sebapa', 'datuk_saudara_sebapa',
      'anak_datuk_saudara_seibu_sebapa'
    ],
    specialConditions: 'Terdinding oleh semua waris asobah yang lebih dekat',
  },
};

/**
 * Heirs grouped by category for UI display
 */
export const HEIRS_BY_CATEGORY: Record<string, HeirType[]> = {
  waris_utama: [
    'suami',
    'isteri',
    'bapa',
    'ibu',
    'anak_lelaki',
    'anak_perempuan',
  ],
  waris_ganti: [
    'datuk',
    'nenek_sebelah_bapa',
    'nenek_sebelah_ibu',
    'cucu_lelaki',
    'cucu_perempuan',
    'cicit_lelaki',
    'cicit_perempuan',
  ],
  waris_kedua: [
    'saudara_seibu_sebapa_lelaki',
    'saudara_seibu_sebapa_perempuan',
    'saudara_sebapa_lelaki',
    'saudara_sebapa_perempuan',
    'saudara_seibu_lelaki',
    'saudara_seibu_perempuan',
    'anak_saudara_seibu_sebapa_lelaki',
    'anak_saudara_sebapa_lelaki',
    'bapa_saudara_seibu_sebapa',
    'bapa_saudara_sebapa',
    'anak_bapa_saudara_seibu_sebapa',
    'anak_bapa_saudara_sebapa',
    'datuk_saudara_seibu_sebapa',
    'datuk_saudara_sebapa',
    'anak_datuk_saudara_seibu_sebapa',
    'anak_datuk_saudara_sebapa',
  ],
};

/**
 * Category labels
 */
export const CATEGORY_LABELS: Record<string, { ms: string; en: string }> = {
  waris_utama: { ms: 'Waris Utama', en: 'Primary Heirs' },
  waris_ganti: { ms: 'Waris Ganti', en: 'Substitute Heirs' },
  waris_kedua: { ms: 'Waris Kedua', en: 'Secondary Heirs' },
};
